/** Which lifecycle a frame belongs to, and the series it continues across lifecycles. */
export interface LifecycleHandoff {
  /** Market, metric, render mode and outcome set, without the lifecycle or timeframe. */
  readonly key: string;
  readonly locked: boolean;
}

export interface MetricChartTransitionFrame {
  readonly seriesKey: string;
  readonly metricTransitionGroupKey: string;
  readonly lifecycleHandoff?: LifecycleHandoff;
}

export interface MetricChartTransition<TFrame extends MetricChartTransitionFrame> {
  readonly source: TFrame | null;
  readonly showTarget: boolean;
  readonly commitTarget: boolean;
}

/**
 * Retain only a compatible metric frame while its replacement becomes ready.
 * The two semantic units keep separate renderer instances and crossfade later.
 * A live market that resolves into its locked series is compatible too: the
 * last live frame stays until the resolved series is ready instead of the chart
 * dropping to its loading state.
 */
export function resolveMetricChartTransition<TFrame extends MetricChartTransitionFrame>(args: {
  readonly committed: TFrame | null;
  readonly target: TFrame;
  readonly targetReady: boolean;
  readonly completedSeriesKey: string | null;
}): MetricChartTransition<TFrame> {
  const { committed, target, targetReady, completedSeriesKey } = args;
  const source =
    committed !== null &&
    committed.seriesKey !== target.seriesKey &&
    (committed.metricTransitionGroupKey === target.metricTransitionGroupKey ||
      isResolutionHandoff(committed.lifecycleHandoff, target.lifecycleHandoff)) &&
    completedSeriesKey !== target.seriesKey
      ? committed
      : null;

  return {
    source,
    showTarget: source === null || targetReady,
    commitTarget: targetReady && source === null,
  };
}

function isResolutionHandoff(
  from: LifecycleHandoff | undefined,
  to: LifecycleHandoff | undefined,
): boolean {
  return from !== undefined && to !== undefined && !from.locked && to.locked && from.key === to.key;
}
